import React, { useState, useEffect } from 'react';
import ReferralFlow from '../components/ReferralFlow';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import {
  CheckIcon, StarIcon, CreditCardIcon,
  ArrowDownCircleIcon, ArrowUpCircleIcon, XCircleIcon,
} from '@heroicons/react/24/outline';

const PLANS = [
  {
    key: 'starter',
    label: 'Starter',
    price: '$5',
    period: '/month',
    tagline: 'Keep your data. Stay connected.',
    features: [
      'View all past results & history',
      'Upload 2 new reports/month',
      'Plain-language explanations',
      'Secure, HIPAA-aligned storage',
    ],
  },
  {
    key: 'active',
    label: 'Full Access',
    price: '$25',
    period: '/month',
    tagline: 'Everything you need, unlimited.',
    recommended: true,
    features: [
      'Unlimited report uploads',
      'Trend tracking across visits',
      'Appointment prep assistant',
      'Medication interaction check',
      'Preventive care gaps (USPSTF)',
      'Ask questions about any report',
      'Priority support',
    ],
  },
];

function ConfirmModal({ open, title, message, confirmLabel, confirmClass, onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] px-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">{title}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Keep plan
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 text-white ${confirmClass}`}
          >
            {loading ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SubscriptionPage() {
  const { user } = useAuth();
  const { dark } = useTheme();
  const navigate = useNavigate();

  const [billingStatus, setBillingStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Modals
  const [modal, setModal] = useState(null); // 'cancel' | 'downgrade' | 'upgrade'

  useEffect(() => {
    api.get('/billing/status')
      .then(r => setBillingStatus(r.data))
      .catch(() => setBillingStatus(null))
      .finally(() => setStatusLoading(false));
  }, []);

  const currentStatus = user?.subscriptionStatus;
  const hasStripeCustomer = billingStatus?.hasStripeCustomer === true;
  const hasStripeSubscription = billingStatus?.hasStripeSubscription === true;

  const statusLabel = {
    trial: `Free Trial${billingStatus?.trialDaysRemaining != null ? ` — ${billingStatus.trialDaysRemaining} days left` : ''}`,
    starter: 'Starter — $5/month',
    active: 'Full Access — $25/month',
    cancelled: 'Cancelled',
    expired: 'Expired',
    past_due: 'Past Due',
  }[currentStatus] || 'Free Trial';

  const statusColor = {
    trial: 'text-sky-600 dark:text-sky-400',
    starter: 'text-slate-700 dark:text-slate-300',
    active: 'text-sky-600 dark:text-sky-400',
    cancelled: 'text-red-500 dark:text-red-400',
    expired: 'text-red-500 dark:text-red-400',
    past_due: 'text-amber-600 dark:text-amber-400',
  }[currentStatus] || 'text-slate-500';

  // ── Stripe portal (manage billing, cancel, downgrade) ──
  async function openPortal() {
    if (!hasStripeSubscription) {
      setActionError('No billing account found. Please contact support if you believe this is an error.');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      const res = await api.post('/billing/create-portal-session');
      window.location.href = res.data.url;
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not open billing portal. Please try again.');
      setActionLoading(false);
    }
  }

  // ── Upgrade to Full Access ──
  async function handleUpgradeConfirm() {
    setActionLoading(true);
    setActionError('');
    try {
      const res = await api.post('/billing/create-checkout-session');
      window.location.href = res.data.url;
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not start checkout. Please try again.');
      setActionLoading(false);
      setModal(null);
    }
  }

  // ── Upgrade trial/expired → Starter ──
  async function handleStarterConfirm() {
    setActionLoading(true);
    setActionError('');
    try {
      const res = await api.post('/billing/create-starter-checkout-session');
      window.location.href = res.data.url;
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not start checkout. Please try again.');
      setActionLoading(false);
      setModal(null);
    }
  }

  // ── Downgrade / Cancel → Stripe portal ──
  async function handlePortalAction() {
    setModal(null);
    await openPortal();
  }

  // ── Self-downgrade for admin-granted users (no Stripe customer) ──
  async function handleSelfDowngrade(targetStatus) {
    setActionLoading(true);
    setActionError('');
    setModal(null);
    try {
      await api.post('/billing/self-downgrade', { targetStatus });
      // Refresh user so status card updates immediately
      window.location.reload();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not update your subscription. Please try again.');
      setActionLoading(false);
    }
  }

  function renderPlanAction(plan) {
    const isCurrent =
      (plan.key === 'starter' && currentStatus === 'starter') ||
      (plan.key === 'active' && currentStatus === 'active');

    if (isCurrent) {
      return (
        <div className="flex flex-col gap-2">
          <div className="w-full flex items-center justify-center gap-2 bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-300 font-semibold py-3 rounded-xl text-sm">
            <CheckIcon className="w-4 h-4" /> Current plan
          </div>
          {currentStatus === 'active' && (hasStripeCustomer ? (
            <button
              onClick={() => setModal('cancel')}
              className="w-full text-red-500 dark:text-red-400 text-xs py-2 hover:underline"
            >
              Cancel subscription
            </button>
          ) : (
            <button
              onClick={() => setModal('admin_cancel_active')}
              className="w-full text-red-500 dark:text-red-400 text-xs py-2 hover:underline"
            >
              Cancel subscription
            </button>
          ))}
          {currentStatus === 'starter' && (hasStripeCustomer ? (
            <button
              onClick={() => setModal('downgrade_cancel')}
              className="w-full text-red-500 dark:text-red-400 text-xs py-2 hover:underline"
            >
              Cancel subscription
            </button>
          ) : (
            <button
              onClick={() => setModal('admin_cancel_starter')}
              className="w-full text-red-500 dark:text-red-400 text-xs py-2 hover:underline"
            >
              Cancel subscription
            </button>
          ))}
        </div>
      );
    }

    if (plan.key === 'active') {
      // Upgrade to Full Access (from trial/starter/expired)
      return (
        <button
          onClick={() => setModal('upgrade')}
          disabled={actionLoading}
          className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 text-sm"
        >
          {currentStatus === 'starter' ? 'Upgrade to Full Access' : 'Subscribe — Full Access'}
        </button>
      );
    }

    if (plan.key === 'starter') {
      if (currentStatus === 'active' && hasStripeCustomer) {
        // Downgrade via Stripe portal
        return (
          <button
            onClick={() => setModal('downgrade')}
            disabled={actionLoading}
            className="w-full border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60 text-sm"
          >
            Downgrade to Starter
          </button>
        );
      }
      if (currentStatus === 'active' && !hasStripeCustomer) {
        // Downgrade via direct DB flip (admin-granted user)
        return (
          <button
            onClick={() => setModal('admin_downgrade')}
            disabled={actionLoading}
            className="w-full border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60 text-sm"
          >
            Downgrade to Starter
          </button>
        );
      }
      // Trial / expired / cancelled → subscribe to starter
      return (
        <button
          onClick={() => setModal('starter')}
          disabled={actionLoading || currentStatus === 'starter'}
          className="w-full bg-slate-800 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 text-sm"
        >
          Get Starter plan
        </button>
      );
    }

    return null;
  }

  const MODALS = {
    upgrade: {
      title: 'Upgrade to Full Access?',
      message: "You'll be charged $25/month. You can cancel any time from this page. All premium features unlock immediately after payment.",
      confirmLabel: 'Upgrade — $25/mo',
      confirmClass: 'bg-sky-600 hover:bg-sky-700',
      onConfirm: handleUpgradeConfirm,
    },
    starter: {
      title: 'Switch to Starter plan?',
      message: "You'll be charged $5/month. You'll keep your data and history, with 2 uploads per month. Upgrade to Full Access any time.",
      confirmLabel: 'Get Starter — $5/mo',
      confirmClass: 'bg-slate-800 hover:bg-slate-700',
      onConfirm: handleStarterConfirm,
    },
    downgrade: {
      title: 'Downgrade to Starter?',
      message: "You'll lose access to premium features (appointment prep, medication check, care gaps, unlimited uploads). Your data is safe. You can upgrade again any time.",
      confirmLabel: 'Downgrade to Starter',
      confirmClass: 'bg-amber-600 hover:bg-amber-700',
      onConfirm: handlePortalAction,
    },
    cancel: {
      title: 'Cancel your subscription?',
      message: "Your Full Access remains active until the end of your billing period. After that, all premium features will be disabled. You can resubscribe any time.",
      confirmLabel: 'Cancel subscription',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: handlePortalAction,
    },
    downgrade_cancel: {
      title: 'Cancel your Starter plan?',
      message: "Your Starter plan stays active until the end of your billing period. After that, you'll lose access to uploads and history. You can resubscribe any time.",
      confirmLabel: 'Cancel subscription',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: handlePortalAction,
    },
    admin_downgrade: {
      title: 'Downgrade to Starter?',
      message: "You'll lose access to premium features (appointment prep, medication check, care gaps, unlimited uploads). Your data stays safe. To get Full Access again, you'll need to subscribe through the normal checkout process.",
      confirmLabel: 'Downgrade to Starter',
      confirmClass: 'bg-amber-600 hover:bg-amber-700',
      onConfirm: () => handleSelfDowngrade('starter'),
    },
    admin_cancel_active: {
      title: 'Cancel your subscription?',
      message: "Your account will be set to cancelled immediately. All premium features will be disabled. To resubscribe, you'll need to go through the normal checkout process.",
      confirmLabel: 'Cancel subscription',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: () => handleSelfDowngrade('cancelled'),
    },
    admin_cancel_starter: {
      title: 'Cancel your Starter plan?',
      message: "Your account will be set to cancelled immediately. You'll lose access to uploads and history. To resubscribe, you'll need to go through the normal checkout process.",
      confirmLabel: 'Cancel subscription',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: () => handleSelfDowngrade('cancelled'),
    },
  };

  const activeModal = modal ? MODALS[modal] : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">My Subscription</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Manage your plan, billing, and payment details.</p>
      </div>

      {/* Status card */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">Current Plan</p>
            {statusLoading ? (
              <div className="h-6 w-40 bg-slate-100 dark:bg-slate-700 rounded animate-pulse" />
            ) : (
              <p className={`text-lg font-bold ${statusColor}`}>{statusLabel}</p>
            )}
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{user?.email}</p>
          </div>
          {(currentStatus === 'active' || currentStatus === 'starter') && hasStripeSubscription && (
            <button
              onClick={openPortal}
              disabled={actionLoading}
              className="flex items-center gap-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-60"
            >
              <CreditCardIcon className="w-4 h-4" />
              Manage billing
            </button>
          )}
        </div>
        {(currentStatus === 'past_due') && (
          <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-4 py-3">
            <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">⚠️ Your payment is past due. Please update your billing details to restore access.</p>
            {hasStripeSubscription && <button onClick={openPortal} className="mt-2 text-sm text-amber-700 dark:text-amber-300 font-semibold underline">Update payment method →</button>}
          </div>
        )}
        {(currentStatus === 'cancelled' || currentStatus === 'expired') && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
            <p className="text-sm text-red-700 dark:text-red-300 font-medium">Your subscription has ended. Choose a plan below to resubscribe.</p>
          </div>
        )}
      </div>

      {/* Error */}
      {actionError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3 mb-6">
          <p className="text-sm text-red-600 dark:text-red-400">{actionError}</p>
        </div>
      )}

      {/* Plan cards */}
      <div className="grid sm:grid-cols-2 gap-5 mb-8">
        {PLANS.map((plan) => {
          const isCurrent =
            (plan.key === 'starter' && currentStatus === 'starter') ||
            (plan.key === 'active' && currentStatus === 'active');

          return (
            <div
              key={plan.key}
              className={`bg-white dark:bg-slate-800 rounded-2xl border-2 overflow-hidden flex flex-col transition-shadow ${
                isCurrent
                  ? 'border-sky-500 shadow-lg shadow-sky-100 dark:shadow-none'
                  : plan.recommended
                  ? 'border-sky-200 dark:border-sky-900'
                  : 'border-slate-200 dark:border-slate-700'
              }`}
            >
              {/* Plan header */}
              <div className={`px-5 pt-5 pb-4 ${plan.recommended ? 'bg-sky-600' : 'bg-slate-800'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {plan.recommended && <StarIcon className="w-4 h-4 text-amber-300" />}
                  <span className={`text-xs font-bold uppercase tracking-widest ${plan.recommended ? 'text-sky-100' : 'text-slate-300'}`}>
                    {plan.label}
                  </span>
                  {isCurrent && (
                    <span className="ml-auto bg-white/20 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <div className="flex items-end gap-1">
                  <span className="text-3xl font-bold text-white">{plan.price}</span>
                  <span className={`mb-1 text-sm ${plan.recommended ? 'text-sky-200' : 'text-slate-400'}`}>{plan.period}</span>
                </div>
                <p className={`text-sm mt-1 ${plan.recommended ? 'text-sky-100' : 'text-slate-400'}`}>{plan.tagline}</p>
              </div>

              {/* Features */}
              <div className="px-5 py-4 flex flex-col flex-1">
                <ul className="space-y-2 mb-5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.recommended ? 'text-sky-500' : 'text-slate-500'}`} />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  {renderPlanAction(plan)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Billing portal shortcut */}
      {(currentStatus === 'active' || currentStatus === 'starter') && hasStripeSubscription && (
        <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <h3 className="font-semibold text-slate-700 dark:text-slate-200 mb-1">Billing portal</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Update your payment method, download invoices, or manage your subscription directly through our secure billing provider.
          </p>
          <button
            onClick={openPortal}
            disabled={actionLoading}
            className="flex items-center gap-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors disabled:opacity-60"
          >
            <CreditCardIcon className="w-4 h-4" />
            Open billing portal →
          </button>
        </div>
      )}

      {/* Staff and clinic codes. Lives here because a nurse told "your employer
          covers this" comes looking under billing, not under profile. */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
          Clinic or employer code
        </h2>
        <ReferralFlow variant="card" />
      </div>

      {/* Confirmation modal */}
      <ConfirmModal
        open={!!activeModal}
        title={activeModal?.title}
        message={activeModal?.message}
        confirmLabel={activeModal?.confirmLabel}
        confirmClass={activeModal?.confirmClass}
        onConfirm={activeModal?.onConfirm}
        onCancel={() => setModal(null)}
        loading={actionLoading}
      />
    </div>
  );
}
